import React, { useState, useEffect } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import {
  Button,
  Divider,
  Grid,
  Switch,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Box,
} from '@material-ui/core';
import {
  Edit,
  GetApp,
  Info,
  InfoOutlined,
  MoreVert,
  Description,
} from '@material-ui/icons';
import { observer } from 'mobx-react';
import { amber, green } from '@material-ui/core/colors';
import { formatCurrency } from '@roc/ui/utils';
import { format as formatDate } from 'date-fns';
import {
  StandardDialog,
  ConfirmDialog,
  DateField,
  CurrencyField,
  FileUploadArea,
  NumberFormat,
  StatusDiv,
} from '@roc/ui';
import { Alert } from '@material-ui/lab';
import { useStore } from '@roc/client-portal-shared/hooks';
import {
  LiquidityBreakdown,
  LiquidityEstimateDisclaimer,
} from './liquidityBreakdown';
import { PreviewBankStatement } from './previewBankStatement';
import { BankStatementYesNoChip } from './bankStatementYesNoChip';
import { OtherSourcesTable } from './otherSourcesTable';
import {
  BankStatementOtherSource,
  BankStatement,
  readHolders,
  formatHolders,
} from '../stores/bankStatementsStore';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  reserveCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2.5),
    backgroundColor: theme.palette.grey[50],
  },
  reserveLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    marginBottom: theme.spacing(0.5),
  },
  reserveAmount: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  borrowerAppliedLiquidity: {
    color: amber[900],
    fontWeight: 600,
  },
  borrowerAppliedLiquidityMulti: {
    color: amber[900],
    fontWeight: 600,
  },
  goalReachedText: {
    color: green[700],
    fontWeight: 600,
  },
  goalReachedLabel: {
    color: green[700],
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  progressBarGoal: {
    height: 8,
    borderRadius: 8,
    backgroundColor: theme.palette.grey[200],
    '& .MuiLinearProgress-bar': {
      backgroundColor: green[300],
      borderRadius: 8,
    },
    marginBottom: theme.spacing(0.5),
  },
  splitTrack: {
    position: 'relative' as const,
    marginBottom: 38,
  },
  splitBarContainer: {
    height: 8,
    borderRadius: 8,
    backgroundColor: theme.palette.grey[200],
    position: 'relative' as const,
    overflow: 'hidden',
  },
  splitTickMark: {
    position: 'absolute' as const,
    top: 0,
    width: 2,
    height: 8,
    backgroundColor: 'white',
    pointerEvents: 'none' as const,
  },
  splitLabel: {
    position: 'absolute' as const,
    top: 12,
    whiteSpace: 'nowrap' as const,
  },
  stillNeeded: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
  },
  progressBar: {
    height: 8,
    borderRadius: 8,
    backgroundColor: theme.palette.grey[200],
    '& .MuiLinearProgress-bar': {
      backgroundColor: amber[500],
      borderRadius: 8,
    },
    marginBottom: theme.spacing(0.5),
  },
  progressLabel: {
    textAlign: 'right',
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
  },
  tableTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
  },
  tableContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: 'hidden',
  },
  headerCell: {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.grey[50],
  },
  bodyCell: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  accountMask: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    background: theme.palette.grey[100],
    borderRadius: 4,
    padding: '2px 8px',
    fontSize: '0.85rem',
    fontFamily: 'monospace',
  },
  verifiedSwitch: {
    '& .MuiSwitch-colorPrimary.Mui-checked': {
      color: theme.palette.success.main,
    },
    '& .MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track': {
      backgroundColor: theme.palette.success.main,
    },
  },
  verifiedText: {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    color: theme.palette.success.dark,
  },
  unverifiedText: {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    color: theme.palette.text.disabled,
  },
  documentLink: {
    color: '#4A90D9',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
    '&:hover': {
      color: '#2C6FAC',
    },
  },
  documentIcon: {
    color: theme.palette.primary.main,
    fontSize: 18,
  },
  downloadIcon: {
    color: theme.palette.text.secondary,
    padding: 4,
  },
  menuIcon: {
    fontSize: 16,
    marginRight: theme.spacing(1),
  },
  deleteItem: {
    color: theme.palette.error.main,
  },
  submitButton: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  infoIconButton: {
    padding: 4,
    color: theme.palette.primary.main,
    '& svg': {
      fontSize: '1.4rem',
    },
  },
  reserveAmountRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  statusSectionLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase' as const,
    marginBottom: theme.spacing(1),
  },
  statusCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
  },
  statusCardActive: {
    border: `1px solid ${green[200]}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    backgroundColor: green[50],
  },
  statusCardContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  statusSwitch: {
    '& .MuiSwitch-colorPrimary.Mui-checked': {
      color: '#43A047',
    },
    '& .MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#43A047',
    },
    '& .MuiSwitch-colorPrimary': {
      color: '#9E9E9E',
    },
    '& .MuiSwitch-track': {
      backgroundColor: '#9E9E9E',
    },
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(16, 2),
  },
  emptyStateIcon: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: theme.palette.grey[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStateTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  tooltipContent: {
    fontSize: '0.875rem',
    padding: '4px 2px',
    display: 'inline-block',
  },
}));

export interface BankStatementDashboardProps {
  loanId: number;
  borrowerId?: number;
  fromSummary?: boolean;
  hideUpload?: boolean;
}

export interface BankStatementDashboardContentProps {
  loanId: number;
  borrowerId?: number;
  fromSummary?: boolean;
  hideUpload?: boolean;
  handleSubmit: () => Promise<void>;
}

const BankStatementDashboardContent = observer(
  ({
    loanId,
    borrowerId,
    fromSummary,
    hideUpload,
    handleSubmit,
  }: BankStatementDashboardContentProps) => {
    const classes = useStyles();
    const { bankStatementsStore, globalStore, loanStore } = useStore();
    const {
      requiredAmount,
      borrowerAppliedLiquidity,
      totalAppliedLiquidity,
      bankStatements,
      status,
      multipleBorrowers,
      borrowerName,
      showBreakdown,
    } = bankStatementsStore;

    const loanDetails = loanStore.loanDetails;
    useEffect(() => {
      if (showBreakdown && !loanDetails) {
        loanStore.fetchLoanDetails(loanId);
      }
    }, [loanId, showBreakdown]);
    const cashoutDelta =
      loanDetails?.liquidityRequiredCalculationValues?.delta ?? 0;

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [activeRowId, setActiveRowId] = useState<number | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [liquidityModalOpen, setLiquidityModalOpen] = useState(false);
    const [
      previewingStatement,
      setPreviewingStatement,
    ] = useState<BankStatement | null>(null);

    const { editFormStore, otherSourceFormStore } = bankStatementsStore;
    const fields = editFormStore.form.fields;
    const otherSourceFields = otherSourceFormStore.form.fields;

    const [otherSourceDialogOpen, setOtherSourceDialogOpen] = useState(false);
    const [editingOtherSourceId, setEditingOtherSourceId] = useState<
      number | null
    >(null);
    const [
      removingOtherSource,
      setRemovingOtherSource,
    ] = useState<BankStatementOtherSource | null>(null);

    const handleEditSave = async () => {
      try {
        await bankStatementsStore.saveBankStatement(editingId);
        setEditingId(null);
      } catch {}
    };

    const showActions =
      !fromSummary && globalStore.isInternalPortal && status !== 'IN_PROGRESS';

    // Not `showActions`: that excludes fromSummary, which is the internal modal.
    const showOtherSources =
      globalStore.isInternalPortal && Boolean(borrowerId);

    const handleOtherSourceAdd = () => {
      bankStatementsStore.loadOtherSourceForm();
      setEditingOtherSourceId(null);
      setOtherSourceDialogOpen(true);
    };

    const handleOtherSourceEdit = (record: BankStatementOtherSource) => {
      bankStatementsStore.loadOtherSourceForm(record);
      setEditingOtherSourceId(record.bankStatementOtherSourceId ?? null);
      setOtherSourceDialogOpen(true);
    };

    const handleOtherSourceSave = async () => {
      if (!borrowerId) return;
      try {
        await bankStatementsStore.saveOtherSource(
          loanId,
          borrowerId,
          editingOtherSourceId ?? undefined
        );
        setOtherSourceDialogOpen(false);
        setEditingOtherSourceId(null);
      } catch {
        // notified by the store
      }
    };

    const handleOtherSourceRemove = async () => {
      const recordId = removingOtherSource?.bankStatementOtherSourceId;
      if (!borrowerId || !recordId) {
        setRemovingOtherSource(null);
        return;
      }
      try {
        await bankStatementsStore.removeOtherSource(
          loanId,
          borrowerId,
          recordId
        );
      } catch {
        // notified by the store
      } finally {
        setRemovingOtherSource(null);
      }
    };

    const hasBorrower = !!borrowerId;

    const borrowerPct =
      requiredAmount > 0
        ? (borrowerAppliedLiquidity / requiredAmount) * 100
        : 0;
    const totalPct =
      requiredAmount > 0 ? (totalAppliedLiquidity / requiredAmount) * 100 : 0;
    const showSplit = hasBorrower && multipleBorrowers;
    const percentage = showSplit
      ? totalPct
      : hasBorrower
      ? borrowerPct
      : totalPct;
    const goalReached = percentage >= 100;
    const borrowerLabelPct = Math.min(borrowerPct, 100);
    const totalLabelPct = Math.min(totalPct, 100);
    const getLabelStyle = (pct: number): React.CSSProperties => {
      if (pct <= 5) return { left: 0, transform: 'none', textAlign: 'left' };
      if (pct >= 95)
        return {
          left: '100%',
          transform: 'translateX(-100%)',
          textAlign: 'right',
        };
      return {
        left: `${pct}%`,
        transform: 'translateX(-50%)',
        textAlign: 'center',
      };
    };
    const showAllBorrowersLabel = totalLabelPct - borrowerLabelPct > 15;
    const rightmostLabelPct = showAllBorrowersLabel
      ? totalLabelPct
      : borrowerLabelPct;
    const showSplitPercentage = rightmostLabelPct < 75;
    const displayedLiquidity = hasBorrower
      ? borrowerAppliedLiquidity
      : totalAppliedLiquidity;
    const stillNeeded = Math.max(
      0,
      requiredAmount - (showSplit ? totalAppliedLiquidity : displayedLiquidity)
    );

    const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, id: number) => {
      setAnchorEl(e.currentTarget);
      setActiveRowId(id);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
      setActiveRowId(null);
    };

    const handleEdit = (id: number) => {
      const statement = bankStatements.find(
        s => s.bankStatementRecordId === id
      );
      if (statement) bankStatementsStore.loadEditForm(statement);
      setEditingId(id);
      handleMenuClose();
    };

    const formatStatementPeriodEnd = (value: string) => {
      if (!value) return '—';
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      return formatDate(date, 'MMMM yyyy');
    };

    const handleDownload = (id: number) => {
      bankStatementsStore.downloadBankStatement(id);
    };

    return (
      <>
        {showBreakdown && (
          <Grid item xs={12}>
            <LiquidityEstimateDisclaimer />
          </Grid>
        )}
        {/* Reserve funds card */}
        <Grid item xs={12}>
          <div className={classes.reserveCard}>
            <Typography className={classes.reserveLabel}>
              Funds You'll Need in Reserve
            </Typography>
            <div className={classes.reserveAmountRow}>
              <Typography className={classes.reserveAmount}>
                {formatCurrency(requiredAmount)}
              </Typography>
              {showBreakdown && (
                <IconButton
                  className={classes.infoIconButton}
                  onClick={() => setLiquidityModalOpen(true)}
                  size="medium"
                >
                  <InfoOutlined />
                </IconButton>
              )}
            </div>
            <Grid container justify="space-between" alignItems="center">
              <Grid item>
                <Typography
                  className={
                    goalReached
                      ? classes.goalReachedText
                      : multipleBorrowers
                      ? classes.borrowerAppliedLiquidityMulti
                      : classes.borrowerAppliedLiquidity
                  }
                >
                  {formatCurrency(displayedLiquidity)} applied
                  {showSplit ? ' (this borrower)' : ''}
                </Typography>
              </Grid>
              <Grid item>
                {goalReached ? (
                  <Typography className={classes.goalReachedLabel}>
                    Goal Reached!
                  </Typography>
                ) : (
                  <Typography className={classes.stillNeeded}>
                    {formatCurrency(stillNeeded)} still needed
                  </Typography>
                )}
              </Grid>
            </Grid>
            {showSplit ? (
              <div className={classes.splitTrack}>
                <div className={classes.splitBarContainer}>
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: `${Math.min(totalPct, 100)}%`,
                      backgroundColor: goalReached ? green[300] : amber[500],
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: `${Math.min(borrowerPct, 100)}%`,
                      backgroundColor: goalReached ? green[700] : amber[900],
                    }}
                  />
                </div>
                {totalAppliedLiquidity > 0 && (
                  <>
                    <div
                      className={classes.splitTickMark}
                      style={{ left: `${Math.min(borrowerPct, 100)}%` }}
                    />
                    <div
                      className={classes.splitLabel}
                      style={getLabelStyle(borrowerLabelPct)}
                    >
                      <Typography
                        variant="caption"
                        style={{
                          display: 'block',
                          color: goalReached ? green[800] : amber[900],
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      >
                        {borrowerName ?? 'Borrower Liquidity'}
                      </Typography>
                      <Typography
                        variant="caption"
                        style={{
                          display: 'block',
                          color: goalReached ? green[700] : amber[900],
                          fontSize: '0.7rem',
                        }}
                      >
                        {formatCurrency(borrowerAppliedLiquidity)}
                      </Typography>
                    </div>
                    {showAllBorrowersLabel && (
                      <div
                        className={classes.splitLabel}
                        style={getLabelStyle(totalLabelPct)}
                      >
                        <Typography
                          variant="caption"
                          style={{
                            display: 'block',
                            color: goalReached ? green[800] : amber[500],
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        >
                          All Borrowers
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{
                            display: 'block',
                            color: goalReached ? green[300] : amber[500],
                            fontSize: '0.7rem',
                          }}
                        >
                          {formatCurrency(totalAppliedLiquidity)}
                        </Typography>
                      </div>
                    )}
                    {!goalReached && showSplitPercentage && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 12,
                          right: 0,
                          textAlign: 'right',
                        }}
                      >
                        <Typography
                          variant="caption"
                          className={classes.progressLabel}
                          style={{ fontSize: '0.7rem' }}
                        >
                          {Math.round(percentage)}% complete
                        </Typography>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <LinearProgress
                variant="determinate"
                value={Math.min(percentage, 100)}
                className={
                  goalReached ? classes.progressBarGoal : classes.progressBar
                }
              />
            )}
            {!goalReached && !showSplit && (
              <Typography
                className={classes.progressLabel}
                style={{ marginTop: 4 }}
              >
                {Math.round(percentage)}% complete
              </Typography>
            )}
          </div>
        </Grid>

        {cashoutDelta < 0 && (
          <Grid item xs={12}>
            <Alert severity="info" icon={<Info />} style={{ fontWeight: 700 }}>
              Estimated cashout on this deal is{' '}
              {formatCurrency(Math.abs(cashoutDelta))}
            </Alert>
          </Grid>
        )}

        {!fromSummary && !hideUpload && (
          <Grid item xs={12}>
            <Typography
              variant="subtitle2"
              style={{ fontWeight: 700, marginBottom: 4 }}
            >
              Please ensure we have two consecutive months of all statements
              uploaded to verify the liquidity requirement
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginBottom: 12 }}
            >
              All pages required · Statements must show account holder name and
              ending balance
            </Typography>
            <FileUploadArea
              items={bankStatementsStore.documents}
              setItems={updateFunc => {
                bankStatementsStore.setDocuments(
                  updateFunc(bankStatementsStore.documents)
                );
                handleSubmit();
              }}
              multiple
            />
          </Grid>
        )}

        <>
          {bankStatements?.length > 0 && status && (
            <Grid item xs={12}>
              <StatusDiv
                status={status}
                text={
                  {
                    IN_PROGRESS:
                      'Processing - This may take a few seconds. Click Refresh to check for results.',
                    SUCCESS:
                      'Documents ready – all uploaded files have been processed',
                    FAILED:
                      'Failed to read the document(s) - needs manual processing',
                  }[status] ?? ''
                }
                onRefresh={() =>
                  bankStatementsStore.fetchBankStatements(loanId, borrowerId)
                }
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography
              className={classes.tableTitle}
              component="div"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              Accounts you've added
            </Typography>
            <TableContainer className={classes.tableContainer}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headerCell}>
                      Account Holders
                    </TableCell>
                    <TableCell
                      className={classes.headerCell}
                      align="center"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Account
                    </TableCell>
                    <TableCell className={classes.headerCell} align="center">
                      Statement Period
                    </TableCell>
                    <TableCell className={classes.headerCell}>
                      Balance
                    </TableCell>
                    <TableCell className={classes.headerCell} align="center">
                      Applied
                    </TableCell>
                    <TableCell className={classes.headerCell}>
                      Document
                    </TableCell>
                    {showActions && (
                      <TableCell className={classes.headerCell} align="center">
                        Actions
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(bankStatements ?? []).map(account => (
                    <TableRow key={account.bankStatementRecordId}>
                      <TableCell className={classes.bodyCell}>
                        {formatHolders(
                          readHolders(account.accountHoldersJson)
                        ) || '—'}
                      </TableCell>
                      <TableCell
                        className={classes.bodyCell}
                        align="center"
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {account.accountLast4 ? (
                          <span className={classes.accountMask}>
                            ···· {account.accountLast4}
                          </span>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell
                        className={classes.bodyCell}
                        align="center"
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {formatStatementPeriodEnd(account.statementPeriodEnd)}
                      </TableCell>
                      <TableCell className={classes.bodyCell}>
                        {formatCurrency(account.endingBalance)}
                      </TableCell>
                      <TableCell className={classes.bodyCell} align="center">
                        <BankStatementYesNoChip
                          value={account.appliedToLiquidity ? 'Yes' : 'No'}
                        />
                      </TableCell>
                      <TableCell className={classes.bodyCell}>
                        {account.originalFileName && (
                          <Tooltip
                            title={
                              account.uploadedDate ? (
                                <span
                                  className={classes.tooltipContent}
                                >{`Uploaded: ${formatDate(
                                  new Date(account.uploadedDate),
                                  'MMMM d, yyyy h:mm a'
                                )}`}</span>
                              ) : (
                                ''
                              )
                            }
                            placement="top"
                          >
                            <span
                              className={classes.documentLink}
                              onClick={() => setPreviewingStatement(account)}
                            >
                              {account.originalFileName}
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                      {showActions && (
                        <TableCell className={classes.bodyCell} align="center">
                          <IconButton
                            size="small"
                            onClick={e =>
                              handleMenuOpen(e, account.bankStatementRecordId)
                            }
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {bankStatements?.length === 0 && (
                <div className={classes.emptyState}>
                  <div className={classes.emptyStateIcon}>
                    <Description style={{ color: '#94A3B8', fontSize: 24 }} />
                  </div>
                  <Typography className={classes.emptyStateTitle}>
                    No accounts added yet
                  </Typography>
                  {!fromSummary && (
                    <Typography variant="body2" color="textSecondary">
                      Upload a bank statement above to get started
                    </Typography>
                  )}
                </div>
              )}
            </TableContainer>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              getContentAnchorEl={null}
            >
              <MenuItem onClick={() => handleEdit(activeRowId)}>
                <Edit className={classes.menuIcon} />
                Edit
              </MenuItem>
            </Menu>
          </Grid>
        </>

        {showOtherSources && (
          <Grid item xs={12}>
            <OtherSourcesTable
              classes={classes}
              showActions={showOtherSources}
              onAdd={handleOtherSourceAdd}
              onEdit={handleOtherSourceEdit}
              onRemove={setRemovingOtherSource}
            />
          </Grid>
        )}

        <StandardDialog
          open={otherSourceDialogOpen}
          title={
            editingOtherSourceId ? 'Edit Other Source' : 'Add Other Source'
          }
          handleClose={() => setOtherSourceDialogOpen(false)}
          maxWidth="sm"
          removeContentBox
          dialogContent={
            <Box m={2}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <CurrencyField
                    testId="otherSourceAmount"
                    label="Amount"
                    required
                    fullWidth
                    variant="outlined"
                    value={otherSourceFields.amount.value as number}
                    onChange={(_, value) =>
                      otherSourceFormStore.onFieldChange('amount', value)
                    }
                    error={!!otherSourceFields.amount.error}
                    errorText={otherSourceFields.amount.error}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Comments"
                    fullWidth
                    multiline
                    minRows={3}
                    variant="outlined"
                    value={otherSourceFields.comments.value}
                    onChange={e =>
                      otherSourceFormStore.onFieldChange(
                        'comments',
                        e.target.value
                      )
                    }
                    error={!!otherSourceFields.comments.error}
                    helperText={otherSourceFields.comments.error}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Grid container justify="flex-end" spacing={1}>
                    <Grid item>
                      <Button
                        variant="outlined"
                        onClick={() => setOtherSourceDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOtherSourceSave}
                        className={classes.submitButton}
                      >
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          }
        />

        <ConfirmDialog
          open={removingOtherSource !== null}
          title="Remove Other Source"
          body={`Are you sure you want to remove this other source of ${formatCurrency(
            removingOtherSource?.amount ?? 0
          )}? It will no longer count toward the borrower's liquidity.`}
          confirmButtonText="Remove"
          cancelButtonText="Cancel"
          handleClose={() => setRemovingOtherSource(null)}
          handleCancel={() => setRemovingOtherSource(null)}
          handleConfirm={handleOtherSourceRemove}
        />

        <StandardDialog
          open={liquidityModalOpen}
          title="Liquidity Breakdown"
          handleClose={() => setLiquidityModalOpen(false)}
          maxWidth="md"
          removeContentBox
          dialogContent={<LiquidityBreakdown loanId={loanId} />}
          dialogActions={
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setLiquidityModalOpen(false)}
            >
              Close
            </Button>
          }
        />

        <StandardDialog
          open={previewingStatement !== null}
          title={previewingStatement?.originalFileName ?? 'Document Preview'}
          handleClose={() => setPreviewingStatement(null)}
          maxWidth="md"
          dialogContent={
            previewingStatement && (
              <PreviewBankStatement statement={previewingStatement} />
            )
          }
          dialogActions={
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetApp />}
              onClick={() =>
                handleDownload(previewingStatement?.bankStatementRecordId)
              }
            >
              Download
            </Button>
          }
        />

        <StandardDialog
          open={editingId !== null}
          title="Edit Bank Statement"
          handleClose={() => setEditingId(null)}
          maxWidth="sm"
          removeContentBox
          dialogContent={
            <Box m={2}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Account Holders"
                    required
                    fullWidth
                    variant="outlined"
                    value={fields.accountHolders.value}
                    onChange={e =>
                      editFormStore.onFieldChange(
                        'accountHolders',
                        e.target.value
                      )
                    }
                    error={!!fields.accountHolders.error}
                    helperText={
                      fields.accountHolders.error ||
                      'Separate multiple holders with a semicolon'
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <NumberFormat
                    testId="accountLast4"
                    label="Account Last 4"
                    required
                    fullWidth
                    customInput={TextField}
                    variant="outlined"
                    format="####"
                    value={fields.accountLast4.value}
                    onValueChange={({ value }) =>
                      editFormStore.onFieldChange('accountLast4', value)
                    }
                    error={!!fields.accountLast4.error}
                    helperText={fields.accountLast4.error}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CurrencyField
                    testId="endingBalance"
                    label="Ending Balance"
                    required
                    fullWidth
                    variant="outlined"
                    value={fields.endingBalance.value as number}
                    onChange={(_, value) =>
                      editFormStore.onFieldChange('endingBalance', value)
                    }
                    error={!!fields.endingBalance.error}
                    errorText={fields.endingBalance.error}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DateField
                    testId="statementPeriodEnd"
                    label="Statement Period End"
                    required
                    fullWidth
                    disableFuture
                    format="MM/dd/yyyy"
                    inputVariant="outlined"
                    value={
                      fields.statementPeriodEnd.value
                        ? new Date(fields.statementPeriodEnd.value as string)
                        : null
                    }
                    onChange={date =>
                      editFormStore.onFieldChange(
                        'statementPeriodEnd',
                        date ? formatDate(date, 'MM/dd/yyyy') : ''
                      )
                    }
                    error={!!fields.statementPeriodEnd.error}
                    helperText={fields.statementPeriodEnd.error}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Typography className={classes.statusSectionLabel}>
                    Status
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <div
                        className={
                          !!fields.appliedToLiquidity.value
                            ? classes.statusCardActive
                            : classes.statusCard
                        }
                      >
                        <div className={classes.statusCardContent}>
                          <div>
                            <Typography style={{ fontWeight: 700 }}>
                              Apply to Liquidity
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Include this account's ending balance in the
                              borrower's reserve calculation.
                            </Typography>
                          </div>
                          <Switch
                            checked={!!fields.appliedToLiquidity.value}
                            onChange={e =>
                              editFormStore.onFieldChange(
                                'appliedToLiquidity',
                                e.target.checked
                              )
                            }
                            color="primary"
                            className={classes.statusSwitch}
                          />
                        </div>
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Grid container justify="flex-end" spacing={1}>
                    <Grid item>
                      <Button
                        variant="outlined"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleEditSave}
                        className={classes.submitButton}
                      >
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          }
        />
      </>
    );
  }
);

export const BankStatementDashboard = observer(
  ({
    loanId,
    borrowerId,
    fromSummary = false,
    hideUpload = false,
  }: BankStatementDashboardProps) => {
    const classes = useStyles();
    const { bankStatementsStore, documentStore } = useStore();
    const { personalGuarantor } = bankStatementsStore;

    useEffect(() => {
      bankStatementsStore.fetchBankStatements(loanId, borrowerId);
    }, [loanId, borrowerId]);

    const handleClose = () => {
      if (fromSummary) {
        window.parent.postMessage('close-modal', '*');
      } else {
        documentStore.documentFormStore.closeCurrentForm();
      }
    };

    const handleSubmit = async () => {
      await bankStatementsStore.uploadFiles(loanId, borrowerId);
    };

    return (
      <Grid container spacing={3} className={classes.root}>
        {borrowerId && !personalGuarantor ? (
          <Grid container spacing={2} className={classes.root}>
            <Grid item xs={12}>
              <Alert
                severity="info"
                icon={<Info />}
                style={{ fontWeight: 700 }}
              >
                This Borrower is not a Guarantor, only Guarantor held assets
                count toward the liquidity requirement.
              </Alert>
            </Grid>
          </Grid>
        ) : (
          <BankStatementDashboardContent
            loanId={loanId}
            borrowerId={borrowerId}
            fromSummary={fromSummary}
            hideUpload={hideUpload}
            handleSubmit={handleSubmit}
          />
        )}
        {!fromSummary && (
          <Grid item xs={12}>
            <Grid container justify="flex-end" spacing={1}>
              <Grid item>
                <Button variant="outlined" onClick={handleClose}>
                  Close
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  }
);
